import { useT } from "@/i18n/LanguageProvider";

function OutilCopy({ t }) {
  return (
    <div className="lg:col-span-5 order-2 lg:order-1">
      <p className="label-eyebrow mb-6">{t("outil.eyebrow")}</p>
      <h2
        data-testid="outil-title"
        className="font-serif font-normal text-[#181A1D] text-4xl lg:text-5xl tracking-tight leading-[1.06] text-balance mb-10"
      >
        {t("outil.titlePre")}
        <em className="italic">{t("outil.titleEm")}</em>
        {t("outil.titlePost")}
      </h2>

      <div className="space-y-6 text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed">
        <p className="[&_strong]:text-[#181A1D] [&_strong]:font-medium">
          {t("outil.p1Pre")}
          <strong>INPI</strong>
          {t("outil.p1Mid")}
          <strong>INSEE</strong>
          {t("outil.p1Post")}
        </p>
        <p>{t("outil.p2")}</p>
      </div>

      <div className="mt-10 pt-8 border-t border-[#E2DEDA] flex items-start gap-4 max-w-xl">
        <span className="font-mono text-[10px] tracking-[0.22em] text-[#8B8F95] mt-1">
          NB.
        </span>
        <p className="text-[13px] text-[#666A70] italic font-normal leading-relaxed">
          {t("outil.note")}
        </p>
      </div>
    </div>
  );
}

function MockupChrome({ m }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-[#E2DEDA] bg-[#FAF9F7]">
      <div className="flex items-center gap-1.5" aria-hidden="true">
        <span className="w-2 h-2 rounded-full bg-[#D8D3CD]" />
        <span className="w-2 h-2 rounded-full bg-[#D8D3CD]" />
        <span className="w-2 h-2 rounded-full bg-[#D8D3CD]" />
      </div>
      <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#666A70] truncate">
        {m.chrome}
      </p>
      <span className="font-mono text-[10px] tracking-[0.18em] text-[#B4B8BD] whitespace-nowrap">
        {m.version}
      </span>
    </div>
  );
}

function MockupSummary({ m }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 border-b border-[#E2DEDA]">
      <div className="px-6 py-5 border-b sm:border-b-0 sm:border-r border-[#E2DEDA]">
        <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95] mb-2">
          {m.sourceLabel}
        </p>
        <p className="font-serif text-base lg:text-lg text-[#181A1D] tracking-tight">
          {m.sourceValue}
        </p>
      </div>
      <div className="px-6 py-5">
        <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95] mb-3">
          {m.exposureLabel}
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-[6px] bg-[#F2F0ED] relative">
            <div className="absolute inset-y-0 left-0 w-[78%] bg-[#181A1D]" />
          </div>
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#181A1D]">
            {m.exposureHigh}
          </span>
        </div>
      </div>
    </div>
  );
}

function MockupTable({ m, rows }) {
  return (
    <div className="px-6 py-5">
      <div className="grid grid-cols-12 gap-3 mb-3 pb-3 border-b border-[#E2DEDA]">
        <p className="col-span-5 font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95]">
          {m.colPlatform}
        </p>
        <p className="col-span-4 font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95]">
          {m.colStatus}
        </p>
        <p className="col-span-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95] text-right sm:text-left">
          {m.colIndex}
        </p>
      </div>

      {rows.map((row, i) => (
        <div
          key={row.name}
          data-testid={`outil-row-${i}`}
          className={`grid grid-cols-12 gap-3 py-3 items-center ${
            i < rows.length - 1 ? "border-b border-[#F2F0ED]" : ""
          }`}
        >
          <p className="col-span-5 text-[13px] lg:text-sm text-[#181A1D] truncate">
            {row.name}
          </p>
          <p className="col-span-4 text-[12px] lg:text-[13px] text-[#5A5E64] font-normal truncate">
            {row.status}
          </p>
          <div className="col-span-3 flex items-center justify-end sm:justify-start gap-[5px]">
            {[1, 2, 3, 4].map((n) => (
              <span
                key={n}
                aria-hidden="true"
                className={`w-1.5 h-1.5 rounded-full ${
                  n <= row.level ? "bg-[#181A1D]" : "bg-[#E2DEDA]"
                }`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MockupActions({ m }) {
  return (
    <div className="px-6 py-4 border-t border-[#E2DEDA] bg-[#FAF9F7] flex flex-wrap items-center justify-between gap-3">
      <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#666A70]">
        {m.actionLabel}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="border border-[#D8D3CD] px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#5A5E64]">
          {m.actionDocument}
        </span>
        <span className="border border-[#181A1D] px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#181A1D]">
          {m.actionQualify}
        </span>
        <span className="bg-[#181A1D] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.18em]">
          {m.actionPrioritize}
        </span>
      </div>
    </div>
  );
}

export default function OutilInterneSection() {
  const { t } = useT();
  const m = t("outil.mockup") || {};

  // Illustrative rows for the internal-tool mockup. Pure UI — no real data.
  const rows = [
    { name: "Pappers.fr", status: m.statusReuse, level: 4 },
    { name: "Societe.com", status: m.statusReuse, level: 3 },
    { name: "Verif.com", status: m.statusReuse, level: 3 },
    { name: "Bodacc", status: m.statusOfficial, level: 2 },
    { name: "Infonet.fr", status: m.statusIndex, level: 4 },
  ];

  return (
    <section
      id="outil"
      data-testid="section-outil"
      className="relative bg-[#FAF9F7] py-24 lg:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <OutilCopy t={t} />

          <div className="lg:col-span-7 order-1 lg:order-2">
            <div
              data-testid="outil-mockup"
              className="bg-white border border-[#E2DEDA] shadow-sm overflow-hidden"
            >
              <MockupChrome m={m} />
              <MockupSummary m={m} />
              <MockupTable m={m} rows={rows} />
              <MockupActions m={m} />
            </div>

            <div className="mt-4 flex items-center justify-between text-[10px] tracking-[0.22em] uppercase text-[#8B8F95]">
              <span>{t("outil.caption")}</span>
              <span>{t("outil.captionRight")}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
